import { useRef } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useNavLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useLocation } from 'react-router-dom';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExternalLink from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';

import SharedAsset from '#client/components/SharedAsset';
import { NON_ENTITY_URLS } from '#client/utils/businessEntity';
import getPageSection from '#client/utils/getPageSection';
import { buildCartUrl, generateZenUrl } from '#client/utils/urls';

import * as Styled from './BusinessEntitySelector.style';
import { EntityMenuItem } from './components/EntityMenuItem';
import { MenuItem } from './components/MenuItem';

import useAnchorElement from '../hooks/useAnchorElement';
import useBusinessEntitySelector from './hooks/useBusinessEntitySelector';

const ctaText = {
  addBusiness: 'Add new business',
  formBusiness: 'Form a new business',
  reserveBusiness: 'Reserve a Business Name',
  viewBusinesses: 'View all companies'
};

function BusinessEntitySelector() {
  const entityMenuRef = useRef(null);
  const buttonRef = useRef(null);
  const enableBusinessNameReservation = useExperimentFlag('enable-business-name-reservation', false);
  const { authenticated } = useAuthentication();
  const { pathname } = useLocation();
  const { loading, currentEntity = {}, businessEntities = [] } = useBusinessEntitySelector();
  const { loading: businessEntityLoading } = useBusinessEntity();
  const { anchorElement, onButtonClick, onMenuClose } = useAnchorElement(entityMenuRef, buttonRef);
  const theme = useTheme();
  const newCompanyLink = '/formation-types-compare/';
  const reserveNameLink = buildCartUrl('entity_name_reservation', {}, { required: [] });
  const viewBusinessesLink = '/d/company';

  const newCompanyText = businessEntities.length === 0 ? ctaText.formBusiness : ctaText.addBusiness;
  const isNonEntityPage = NON_ENTITY_URLS.includes(pathname);
  const hasMultipleEntities = businessEntities.length > 1;

  const baseEventProps = {
    elementName: 'entity_selector_option',
    navLevel: 1,
    pageCategory: 'dashboard',
    pageSection: getPageSection(pathname)
  };

  const handleClickNew = useNavLinkPressedCallback({
    ...baseEventProps,
    elementText: newCompanyText,
    linkDestination: generateZenUrl(newCompanyLink)
  });

  const handleClickReserve = useNavLinkPressedCallback({
    ...baseEventProps,
    elementText: ctaText.reserveBusiness,
    linkDestination: reserveNameLink
  });

  const handleTrackingViewBusinesses = useNavLinkPressedCallback({
    ...baseEventProps,
    elementText: ctaText.viewBusinesses,
    linkDestination: generateZenUrl(viewBusinessesLink)
  });

  const handleClickViewBusinesses = () => {
    handleTrackingViewBusinesses();
    onMenuClose();
  };

  if (!authenticated || isNonEntityPage) {
    return null;
  }

  if (loading || businessEntityLoading) {
    return <Styled.Skeleton height="60px" />;
  }

  return (
    <>
      <Styled.Button
        aria-controls="entity-menu"
        aria-label="entity menu"
        aria-haspopup="menu"
        variant="primary"
        ref={buttonRef}
        onClick={onButtonClick}
        sx={{
          background: anchorElement ? theme.palette.focus90.main : theme.palette.focus10.main,
          color: anchorElement ? theme.palette.focus00.main : theme.palette.focus60.main
        }}
      >
        <Styled.TruncatedText>{currentEntity.name}</Styled.TruncatedText>
        {anchorElement && <ExpandLessIcon aria-hidden />}
        {!anchorElement && <ExpandMoreIcon aria-hidden />}
      </Styled.Button>

      <Styled.Menu
        keepMounted
        aria-expanded={Boolean(anchorElement)}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        open={Boolean(anchorElement)}
        onClose={onMenuClose}
        ref={entityMenuRef}
        elevation={0}
        id="entity-menu"
      >

        {hasMultipleEntities && businessEntities.map((entity, idx) => {
          const entityPath = currentEntity.uuid
            ? pathname.replace(currentEntity.uuid, entity.uuid)
            : `/d/company/${entity.uuid}`;

          const entityItem = {
            ...entity,
            eventProps: {
              ...baseEventProps,
              elementText: entity.name,
              linkDestination: entityPath,
              navIndex: idx + 1
            },
            path: entityPath
          };

          return (
            <EntityMenuItem
              key={entityItem.uuid}
              entity={entityItem}
              onClick={onMenuClose}
            />
          );
        })}

        {hasMultipleEntities && (
          <MenuItem
            onClick={handleClickViewBusinesses}
            variant="view-all"
          >
            <Styled.Link
              end
              role="menuitem"
              to={viewBusinessesLink}
            >
              <span>{ctaText.viewBusinesses}</span>
            </Styled.Link>
          </MenuItem>
        )}

        {hasMultipleEntities && (
          <Styled.Divider />
        )}

        <MenuItem onClick={handleClickNew}>
          <Styled.Link
            as={ExternalLink}
            href={newCompanyLink}
            role="menuitem"
          >
            <SharedAsset
              alt=""
              aria-hidden
              src="/icon/micro/line-plus-thin.svg"
              width={16}
            />
            <span>{newCompanyText}</span>
          </Styled.Link>
        </MenuItem>

        {enableBusinessNameReservation && (
          <MenuItem onClick={handleClickReserve}>
            <Styled.Link
              as={ExternalLink}
              href={reserveNameLink}
              role="menuitem"
            >
              <SharedAsset
                alt=""
                src="/icon/micro/line-label.svg"
                width={16}
              />
              <span>{ctaText.reserveBusiness}</span>
            </Styled.Link>
          </MenuItem>
        )}
      </Styled.Menu>
    </>
  );
}

BusinessEntitySelector.propTypes = {};

export default BusinessEntitySelector;
